/**
 * Fait : décrit les documents des collections `projets` et `membresDeProjet`.
 *   C'est ici que vit la LOI du modèle (critère 7, décision D-010) : le projet
 *   est l'unité de rattachement, et un droit est un document qui porte
 *   OBLIGATOIREMENT son projet — un droit hors projet ne se valide pas.
 *
 * Ne fait pas : il ne décide pas qui a accès à quoi — décider est le métier de
 *   la fonction d'autorisation unique (phase 3) — et n'impose pas l'unicité
 *   d'un membre par projet : cette garantie est un index unique, déclaré au
 *   catalogue des index et prouvé sur base réelle (`npm run donnees`).
 */

import { z } from 'zod';

import { descriptionDonnee, identifiant, nomDonne, versionDeRessource } from '../schemas.ts';
import { NIVEAUX_DE_MEMBRE } from '../schemas-projet.ts';

/** Un projet partagé — l'objet central du produit (décision D-010). */
export const projet = z
  .object({
    nom: nomDonne,

    /**
     * De quoi il retourne, en quelques phrases — « préparation du Noël 2027 ».
     *
     * OPTIONNELLE, et c'est la seule forme possible : les projets créés avant
     * ce champ n'en ont pas, et la migration ne leur en invente pas. Une
     * description vide est ABSENTE plutôt que chaîne vide, pour qu'il n'y ait
     * qu'un seul état « pas de description » à afficher et à tester.
     */
    description: descriptionDonnee.optional(),

    creeParId: identifiant,

    /**
     * Version pour l'arbitrage des écritures concurrentes (D-011) : la seconde
     * écriture sur une version dépassée est refusée et rejouée, jamais
     * appliquée en silence.
     */
    version: versionDeRessource,

    /** Un projet archivé reste lisible ; il ne s'archive pas en s'effaçant. */
    archiveLe: z.date().optional(),

    /**
     * LA CORBEILLE (`D-030`). « Supprimer » pose cette date et rien de plus :
     * le projet quitte la vue de TOUS ses participants, et pas un document
     * n'est effacé — ni ses messages, ni ses documents, ni les droits qui
     * disent qui y travaillait.
     *
     * POURQUOI CE N'EST PAS UN EFFACEMENT, décidé le 2026-08-20 : le geste
     * d'avant détruisait vraiment les conversations et les droits, laissait
     * les messages orphelins, et répondait « c'est fait » même quand il
     * n'avait rien fait. Rendre un projet à quelqu'un qui s'est trompé est
     * impossible une fois les octets partis ; une date se retire.
     *
     * Ce qui VIDE réellement la corbeille appartient au nœud `40.p` — un seul
     * geste, au fondateur, avec ses écrans.
     */
    misALaCorbeilleLe: z.date().optional(),

    /** Qui l'y a mis. Sans ce nom, la corbeille ne se relit pas. */
    misALaCorbeilleParId: identifiant.optional(),

    /**
     * LE RÉFÉRENT (`D-023`) : le parent administrateur — ou le fondateur — vers
     * qui l'on se tourne pour ce projet. Un membre en choisit un en créant son
     * projet, et sans ce choix le projet ne naît pas ; un projet créé par un
     * administrateur n'en a pas besoin, d'où l'optionnalité.
     *
     * POURQUOI LE CHAMP, ET PAS « celui des membres qui est administrateur » :
     * un projet peut compter plusieurs administrateurs parmi ses participants,
     * et le référent est UNE personne désignée, pas une propriété déduite. Les
     * deux verrous de `D-023` se lisent donc ICI, sur ce champ, jamais sur la
     * liste des membres — un verrou qui interroge la liste laisse passer tout
     * projet où l'administrateur est arrivé par un partage ordinaire.
     */
    referentId: identifiant.optional(),

    /**
     * LA MARQUE DE LA MIGRATION 011, et rien d'autre. Elle dit que ce référent
     * a été posé par `D-026` — le fondateur devenu référent des projets
     * antérieurs à la règle — et non choisi par quelqu'un.
     *
     * Sans elle, le démontage de la migration retirerait aussi les référents
     * choisis à la main après coup. Une réversibilité qui ne distingue pas ce
     * qu'elle a posé de ce qu'elle a trouvé n'est pas une réversibilité.
     */
    referentPoseParLaMigration: z.literal(true).optional(),

    /**
     * Les octets déjà stockés par ce projet — un compteur MATÉRIALISÉ, pas
     * une somme recalculée.
     *
     * C'est la leçon du budget, appliquée au stockage : compter les fichiers
     * puis écrire laisserait passer deux dépôts simultanés au-dessus du quota
     * (write skew). Le compteur permet un `$inc` CONDITIONNEL, où la base
     * arbitre en une seule commande.
     */
    octetsStockes: z.number().int().nonnegative().default(0),

    creeLe: z.date(),
    misAJourLe: z.date(),
  })
  .strict();

export type Projet = z.infer<typeof projet>;

/**
 * LE droit du produit : l'appartenance d'un compte à un projet, avec son
 * niveau. `projetId` est requis par construction — c'est la forme que prend le
 * critère 7 dans le modèle. La fonction d'autorisation unique (phase 3) ne
 * fera que LIRE ces documents, jamais en inventer la règle.
 */
export const membreDeProjet = z
  .object({
    projetId: identifiant,
    compteId: identifiant,
    niveau: z.enum(NIVEAUX_DE_MEMBRE),
    ajouteParId: identifiant,
    creeLe: z.date(),

    /**
     * LA MARQUE : le numéro de la migration qui a posé cette participation.
     * Absente quand un humain l'a posée — et c'est tout ce qui protège les
     * données de production le jour d'un démontage.
     *
     * MESURÉ COMME NÉCESSAIRE, pas supposé : la migration 011 inscrit le
     * fondateur comme participant en lecture de chaque projet dont il devient
     * référent (`D-023` : un référent voit ce qu'il suit). Un démontage qui
     * effacerait « les participations du fondateur » retirerait du même geste
     * les projets qu'on lui avait VRAIMENT partagés.
     */
    poseeParLaMigration: z.number().int().positive().optional(),
  })
  .strict();

export type MembreDeProjet = z.infer<typeof membreDeProjet>;
